import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  AdminTable,
  type AdminTableProps,
  ITEMS_PER_PAGE,
} from 'components/AdminTable/AdminTable';
import ObjectTableFilter, {
  type IObjectTableFilter,
} from 'components/ObjectTable/ObjectTableFilter';
import type { TColumns } from 'hooks/useSortColumnsModal';
import { getText } from 'i18n/intl';
import isNumber from 'lodash/isNumber';
import { fieldMappingSelector } from 'slices/field.slice';
import { type TFindObjectsPayload, findMoreObjects } from 'slices/object.slice';
import {
  selectObjectTypesList,
  selectObjectTypesMap,
} from 'slices/objectTypes.slice';
import { organizationMapSelector } from 'slices/organizationHierarchy.slice';
import { useAppSelector } from 'slices/store';
import type { IAPIResult } from 'types/state.interface';
import { isFieldSaveable, isReferenceField } from 'utils/fieldUtils';
import {
  EFieldType,
  type TField,
  type TTEObject,
  type TType,
} from '@timeedit/types';
import { Alert, Button, Icon, Tag } from '@timeedit/ui-components';
import { Flex } from '@timeedit/ui-components/antd';
import type { SorterResult } from '@timeedit/ui-components/antd/table/interface';
import { parseIdSearch, toInternalIds } from './idSearch';
import './ObjectTable.scss';

const GENERAL_COLUMN_WIDTH = 150;
const ACTIVE_COLUMN_WIDTH = 70;

type ColumnKey = 'extId' | 'types' | 'organizations' | 'active';

type TypedAdminTableProps = AdminTableProps<
  ColumnKey,
  TTEObject,
  TFindObjectsPayload
>;

export type ObjectTableProps = Omit<
  TypedAdminTableProps,
  'columns' | 'items' | 'objectTypeId'
> & {
  /** The objects to display in the table */
  objects: TTEObject[];

  /** Function to reset the table, e.g. when filters change */
  resetFn: () => void;

  /** The available object types for filtering */
  availableObjectTypes?: TType[];

  /** Callback when the filter changes */
  onFilterChange?: (
    filters: IObjectTableFilter,
    findObjectManagerPayload: TFindObjectsPayload
  ) => void;

  /** Default selected filter in case it's stored (optional) */
  defaultFilter?: IObjectTableFilter;

  /** Enables filter for selecting if template objects should be included in result */
  allowTemplateObjects?: boolean;

  /** IDs of objects that have been added and should be highlighted */
  changedIds?: string[];

  /** The total amount of items based on the current filter */
  onFilteredTotalChanged?: (count: number) => void;

  /** Whether to show the reload alert after a batch job completes */
  showReloadAlert?: boolean;

  /** Callback to dismiss the reload alert */
  onDismissReloadAlert?: () => void;
};

const getEmptyObjectResponse = (
  payload: TFindObjectsPayload
): Promise<IAPIResult<TTEObject>> =>
  Promise.resolve({
    results: [],
    page: 1,
    limit: payload.limit ?? ITEMS_PER_PAGE,
    totalPages: 0,
    totalResults: 0,
  });

const stringFieldValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'boolean') {
    return String(Number(value));
  }

  return String(value);
};

const SEARCHABLE_FIELD_TYPES: Set<string> = new Set([
  EFieldType.TEXT,
  EFieldType.SIGNATURE,
  EFieldType.TELEPHONE,
  EFieldType.INTEGER,
  EFieldType.REFERENCE,
]);

const useSearchFields = (objectTypeId: number) => {
  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const fieldMap = useAppSelector(fieldMappingSelector);

  const selectedObjectType = useMemo(
    () => objectTypesMap[objectTypeId],
    [objectTypeId, objectTypesMap]
  );

  const searchFields = useMemo(() => {
    if (!selectedObjectType) return [];

    const allFieldIds = [
      ...(selectedObjectType.fields ?? []),
      ...(selectedObjectType.parentFields ?? []),
    ];

    return allFieldIds
      .map((fieldId) => fieldMap[fieldId])
      .filter(
        (field): field is TField =>
          !!field && SEARCHABLE_FIELD_TYPES.has(field.fieldType)
      );
  }, [fieldMap, selectedObjectType]);

  return searchFields;
};

export const useObjectTableColumns = (
  objectTypeId: number
): TypedAdminTableProps['columns'] => {
  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const organizationMap = useAppSelector(organizationMapSelector);
  const fieldMap = useAppSelector(fieldMappingSelector);

  const fieldDisplayValue = useCallback(
    (fieldId: number) => (fieldValue: unknown) => {
      const field = fieldMap[fieldId];

      if (!field) {
        return fieldValue;
      }

      if (field.fieldType === EFieldType.CHECKBOX) {
        return Number(fieldValue)
          ? getText('object_manager.object_table.data.yes')
          : getText('object_manager.object_table.data.no');
      }

      return fieldValue;
    },
    [fieldMap]
  );

  // Static columns that are always present
  const staticColumns: TColumns<ColumnKey, TTEObject> = useMemo(
    () => ({
      extId: {
        label: getText('object_manager.object_table.column.ext_id'),
        key: 'extId',
        width: GENERAL_COLUMN_WIDTH,
        resizable: true,
      },
      types: {
        label: getText('object_manager.object_table.column.types'),
        key: 'types',
        width: GENERAL_COLUMN_WIDTH,
        resizable: true,
        // Render all types in the column using their names, fallback to the type ID if not found
        render: (typeIds: number[]) =>
          typeIds
            .map((typeId) => {
              const type = objectTypesMap[typeId];
              return type ? type.name : typeId;
            })
            .join(', '),
      },
      organizations: {
        label: getText('object_manager.object_table.column.organizations'),
        key: 'organizations',
        width: GENERAL_COLUMN_WIDTH,
        resizable: true,
        // Render all organizations in the column using their names, fallback to the organization ID if not found
        render: (organizationIds: string[]) =>
          organizationIds
            .map((organizationId) => {
              const org = organizationMap[organizationId];
              return org ? org.name : organizationId;
            })
            .join(', '),
      },
      active: {
        label: getText('object_manager.object_table.column.active'),
        key: 'active',
        width: ACTIVE_COLUMN_WIDTH,
        resizable: true,
        render: (active: boolean) => (
          <Tag color={active ? 'green' : 'red'}>
            {active
              ? getText('object_manager.object_table.data.active')
              : getText('object_manager.object_table.data.inactive')}
          </Tag>
        ),
      },
    }),
    [objectTypesMap, organizationMap]
  );

  // Field columns that are dynamically generated based on the object type's fields
  const fieldColumns = useMemo(() => {
    const objectType: TType = objectTypesMap[objectTypeId];

    const fieldIds = [
      ...(objectType?.parentFields ?? []),
      ...(objectType?.fields ?? []),
    ];

    const columnEntries =
      fieldIds
        ?.map((fieldId) => fieldMap[fieldId])
        .filter((field) => isFieldSaveable(field) || isReferenceField(field))
        .map((field) => [
          field.id,
          {
            label: field.name,
            key: field.id,
            width: GENERAL_COLUMN_WIDTH,
            resizable: true,
            sorter: field.sortable ?? false,
            render: (_value: unknown, record: TTEObject) => {
              return record.fields
                .filter((recordField) => recordField.fieldId === field.id)
                .flatMap((recordField) =>
                  recordField.values.map(fieldDisplayValue(recordField.fieldId))
                )
                .join(', ');
            },
          },
        ]) ?? [];

    return Object.fromEntries(columnEntries);
  }, [objectTypeId, objectTypesMap, fieldMap]);

  const columns: TColumns<ColumnKey, TTEObject> = useMemo(
    () => ({
      ...staticColumns,
      ...fieldColumns,
    }),
    [staticColumns, fieldColumns]
  );

  return columns;
};

const defaultTotalItemCount = async (includeTemplateObjects?: boolean) => {
  // Fetch the total number of objects, but limit to 0 to avoid fetching any objects
  const response = await findMoreObjects({
    limit: 0,
    includeTemplateObjects,
  });
  return response.totalResults;
};

const useSelectedObjectType = (filters: IObjectTableFilter) =>
  useMemo(() => {
    const number = Number.parseInt(filters.objectType, 10);
    return isFinite(number) ? number : undefined;
  }, [filters.objectType]);

const useFindObjectsPayload = (
  filters: IObjectTableFilter
): TFindObjectsPayload => {
  const selectedObjectType = useSelectedObjectType(filters);
  const searchFields = useSearchFields(selectedObjectType);

  const findObjectsPayload = useMemo(() => {
    // Reference fields are opt-in on the API and the table renders a column per
    // field, so every read built from this payload must request them.
    const payload: TFindObjectsPayload = { includeReferenceFields: true };

    if (filters.objectType != null) {
      payload.typeId = filters.objectType;
    }

    if (filters.organization != null) {
      payload.organizations = [filters.organization];
    }

    if (filters.active != null) {
      payload.active = filters.active ? 'TRUE' : 'FALSE';
    }

    if (filters.includeTemplateObjects != null) {
      payload.includeTemplateObjects = filters.includeTemplateObjects;
    }

    if (filters.freeTextSearch != null && filters.freeTextSearch.length > 0) {
      const ids = parseIdSearch(filters.freeTextSearch);
      if (ids) {
        payload.extIds = ids;
        const convertedInternalIds = toInternalIds(ids);
        if (convertedInternalIds.length > 0) {
          payload.ids = convertedInternalIds;
        }

        // If searching by IDs, we don't need to include other fields search parameters
        return payload;
      }

      payload.fields = {
        searchText: filters.freeTextSearch,
        generalSearchFieldIds:
          searchFields?.map((field) => field.id).slice(0, 20) ?? [],
      };
    }

    if (filters.fields != null) {
      payload.fields = {
        ...payload.fields,
        exactSearchFields: filters.fields.map((field) => ({
          fieldId: Number(field.fieldId),
          values: field.values.map(stringFieldValue),
        })),
      };
    }

    return payload;
  }, [filters, searchFields]);

  return findObjectsPayload;
};

const useObjectTableFilter = (
  availableObjectTypes: TType[],
  defaultFilter?: IObjectTableFilter,
  allowTemplateObjects?: boolean
) => {
  const allObjectTypes = useSelector(selectObjectTypesList);

  const objectTypesToUse = useMemo(() => {
    let objectTypesToUse = allObjectTypes;

    if (availableObjectTypes) {
      objectTypesToUse = objectTypesToUse.filter((objectType) =>
        availableObjectTypes.includes(objectType)
      );
    }

    return objectTypesToUse;
  }, [allObjectTypes, availableObjectTypes]);

  const preSelectedObjectType = useMemo(
    () =>
      defaultFilter?.objectType ??
      objectTypesToUse?.[0]?.id?.toString() ??
      null,
    [objectTypesToUse, defaultFilter?.objectType]
  );

  const [filters, setFilters] = useState<IObjectTableFilter>({
    active: true,
    includeTemplateObjects: allowTemplateObjects ? false : undefined,
    objectType: preSelectedObjectType,
    ...defaultFilter,
  });

  useEffect(() => {
    // Update the selected object type when the pre-selected object type changes
    setFilters((prevFilters) => ({
      ...prevFilters,
      objectType: preSelectedObjectType,
    }));
  }, [preSelectedObjectType]);

  return {
    filters,
    filterComponent: (
      <ObjectTableFilter
        filters={filters}
        onChange={setFilters}
        availableObjectTypes={objectTypesToUse}
        allowTemplateObjects={allowTemplateObjects}
      />
    ),
  };
};

/**
 * Table component for displaying and managing objects.
 */
export const ObjectTable = ({
  objects,
  contextMenuActions,
  onRow,
  fetchFn,
  resetFn,
  totalItemCount,
  availableObjectTypes,
  onFilterChange,
  defaultFilter,
  allowTemplateObjects,
  changedIds,
  showReloadAlert,
  onDismissReloadAlert,
  ...props
}: ObjectTableProps) => {
  const { filters, filterComponent } = useObjectTableFilter(
    availableObjectTypes,
    defaultFilter,
    allowTemplateObjects
  );
  const [sorter, setSorter] = useState<SorterResult<TTEObject> | null>(null);

  useEffect(() => {
    // Trigger reset when filters change
    resetFn();
  }, [filters, resetFn, sorter]);

  const findObjectManagerPayload = useFindObjectsPayload(filters);

  useEffect(() => {
    // Updates the payload for finding objects used by e.g. the drawer when saving an object and re-fetching the objects with the new data
    onFilterChange?.(filters, findObjectManagerPayload);
  }, [onFilterChange, filters]);

  const selectedObjectType = useSelectedObjectType(filters);
  const columns: TypedAdminTableProps['columns'] =
    useObjectTableColumns(selectedObjectType);

  const fetchObjectsWithFilters: TypedAdminTableProps['fetchFn'] = useCallback(
    (options: TFindObjectsPayload) => {
      const optionsWithFilter: TFindObjectsPayload = {
        ...findObjectManagerPayload,
        ...options,
      };

      // Having no object type to filter on should result in no objects being fetched (but the API will return all objects matching the rest of the filters)
      if (optionsWithFilter.typeId == null) {
        return getEmptyObjectResponse(optionsWithFilter);
      }

      // If there is a search text but no general search field IDs, the search text doesn't match anything (but the API will return all objects matching the rest of the filters)
      if (
        (optionsWithFilter.fields?.searchText?.length ?? 0) > 0 &&
        optionsWithFilter.fields.generalSearchFieldIds?.length === 0
      ) {
        return getEmptyObjectResponse(optionsWithFilter);
      }

      const hasSorter = typeof sorter?.columnKey === 'number';

      // Check if the fieldId is valid for the current object type
      const hasValidSort =
        hasSorter &&
        Object.values(columns).find((p) => p.key === sorter.columnKey);

      if (hasValidSort && sorter.order) {
        optionsWithFilter.sort =
          sorter.order === 'ascend' ? 'ASCFIELD' : 'DESCFIELD';
        optionsWithFilter.sortFieldId = sorter.columnKey as number;
      }

      return fetchFn(optionsWithFilter);
    },
    [fetchFn, findObjectManagerPayload, columns, sorter]
  );

  // Calculate the actual total item count based on the provided totalItemCount prop
  const actualTotalItemCount: TypedAdminTableProps['totalItemCount'] =
    useMemo(() => {
      if (isNumber(totalItemCount)) {
        return totalItemCount;
      }

      if (typeof totalItemCount === 'function') {
        return totalItemCount;
      }

      return () => defaultTotalItemCount(allowTemplateObjects);
    }, [totalItemCount, allowTemplateObjects]);

  type OnChange = NonNullable<
    AdminTableProps<ColumnKey, TTEObject>['onChange']
  >;
  const onChange: OnChange = useCallback<OnChange>(
    (pagination, filters, sorter, extra) => {
      setSorter(Array.isArray(sorter) ? sorter[0] : sorter);

      props.onChange?.(pagination, filters, sorter, extra);
    },
    [props.onChange]
  );

  return (
    <Flex vertical className="object-table">
      {filterComponent}
      {showReloadAlert && (
        <Alert
          className="warning-reload-table"
          type="warning"
          title={getText('object_manager.object_table.update.description')}
          showIcon
          action={
            <Button
              icon={<Icon id="ReloadOutlined" alt="reload" />}
              onClick={() => {
                resetFn();
                onDismissReloadAlert?.();
              }}
            >
              {getText('object_manager.object_table.update.button')}
            </Button>
          }
        />
      )}
      <AdminTable<ColumnKey, TTEObject>
        {...props}
        changedIds={changedIds}
        onChange={onChange}
        columns={columns}
        items={objects}
        contextMenuActions={contextMenuActions}
        onRow={onRow}
        fetchFn={fetchObjectsWithFilters}
        totalItemCount={actualTotalItemCount}
        objectTypeId={selectedObjectType}
      />
    </Flex>
  );
};
